import { useState } from "react";
import { useGT } from "gt-react";
import { Copy, Check, ChevronDown, ChevronRight, Eye, EyeOff } from "lucide-react";

export const SAFE_HEADERS = new Set(["content-type", "accept", "content-length", "cache-control", "user-agent"]);

export function maskValue(value: string): string {
  if (value.length <= 4) return "****";
  return value.slice(0, 3) + "****" + value.slice(-3);
}

export function HeadersTable({ headers }: { headers: Record<string, string> }) {
  const t = useGT();
  const [revealed, setRevealed] = useState(false);
  const entries = Object.entries(headers);
  if (entries.length === 0) {
    return <span className="text-[11px] text-zinc-600">{t("No headers")}</span>;
  }
  const hasSensitive = entries.some(([k]) => !SAFE_HEADERS.has(k.toLowerCase()));
  return (
    <div>
      {hasSensitive && (
        <div className="mb-1 flex justify-end">
          <button
            onClick={() => setRevealed((v) => !v)}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
          >
            {revealed ? <EyeOff size={10} /> : <Eye size={10} />}
            {revealed ? t("Hide values") : t("Reveal values")}
          </button>
        </div>
      )}
      <div className="overflow-x-auto rounded bg-surface-raised">
        <table className="w-full text-[11px]">
          <tbody>
            {entries.map(([key, value], i) => {
              const isSafe = SAFE_HEADERS.has(key.toLowerCase());
              const displayValue = isSafe || revealed ? value : maskValue(value);
              return (
                <tr key={key} className={i < entries.length - 1 ? "border-b border-border/50" : ""}>
                  <td className="whitespace-nowrap px-2.5 py-1.5 align-top font-mono font-medium text-accent">{key}</td>
                  <td className="break-all px-2.5 py-1.5 font-mono text-zinc-300">{displayValue}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Section({ title, badge, defaultOpen = false, children }: { title: string; badge?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="shrink-0 border-b border-border">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-4 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
      >
        {open ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
        {title}
        {badge}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const t = useGT();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
        copied
          ? "bg-success-bg text-success"
          : "text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
      }`}
      title={t("Copy to clipboard")}
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
      {copied ? t("Copied") : label ?? t("Copy")}
    </button>
  );
}

export function tryFormatJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

export function buildCurl(debug: { request: { method: string; url: string; headers: Record<string, string>; body?: string } }): string {
  const parts = [`curl -X ${debug.request.method}`];
  parts.push(`  '${debug.request.url}'`);
  for (const [k, v] of Object.entries(debug.request.headers)) {
    parts.push(`  -H '${k}: ${v}'`);
  }
  if (debug.request.body) {
    parts.push(`  -d '${debug.request.body.replace(/'/g, "'\\''")}'`);
  }
  return parts.join(" \\\n");
}
